/**
 * Savings Calculator capture form — the card-rates comparison on / and
 * /switch-and-save. One form replaces the old inline review form and the
 * "email me the results" button.
 *
 * The calculator's inputs and results arrive as `context` (built by
 * savingsContext below, so the keys contextToCrm reads stay in one place).
 * Turnover, ATV and the visitor's current rates/fees pre-fill CRM fields;
 * everything else is kept in the Lead's Note.
 */
import { CARD_BANDS, DEFAULT_MIX_SAVINGS_BASIC } from "@/utils/pricingEngine";
import { company, currentProvider, email, fullName, phone } from "./common";

/** Legacy analytics name kept so quote_requested / email_capture reports continue. */

const K = {
  turnover: "Monthly card turnover (£)",
  atv: "Average transaction value (£)",
  debit: "Current debit rate (%)",
  credit: "Current credit rate (%)",
  auth: "Current auth fee (£ per transaction)",
  terminal: "Terminal rental (£/month)",
  pci: "PCI fee (£/month)",
  other: "Other fees (£/month)",
  current: "Current processing cost",
  proposed: "CanDo processing estimate",
};

const gbp = (n) =>
  `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const money = (n) => `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 3 })}`;
const num = (v) => (typeof v === "number" && v > 0 ? v : null);

/** Readable context for the Note; `inputs` are the calculator's raw strings. */
export function savingsContext(inputs, results, advancedMode) {
  if (!results) return {};
  const entered = (key) => parseFloat(inputs[key]) || null;
  const share = (key, fallback) => {
    const value = entered(key);
    return value ? `${value}%` : `${fallback}% (default)`;
  };
  const mix = advancedMode
    ? CARD_BANDS.map((b) => `${b.label} ${parseFloat(inputs[`mix_${b.key}`]) || 0}%`).join(", ")
    : null;
  return {
    Mode: advancedMode ? "Advanced (6-band card mix)" : "Basic (debit + credit)",
    [K.turnover]: entered("monthlyTurnover"),
    [K.atv]: entered("avgTransaction"),
    "Debit card share": advancedMode ? null : share("debitPercent", DEFAULT_MIX_SAVINGS_BASIC.debit),
    "Credit card share": advancedMode ? null : share("creditPercent", DEFAULT_MIX_SAVINGS_BASIC.credit),
    "Card mix": mix,
    [K.debit]: entered("currentDebitRate"),
    [K.credit]: entered("currentCreditRate"),
    [K.auth]: entered("currentAuthFee"),
    [K.terminal]: entered("terminalRental"),
    [K.pci]: entered("pciFee"),
    [K.other]: entered("otherFees"),
    [K.current]: `${gbp(results.currentProcessing)}/month`,
    [K.proposed]: `${gbp(results.proposedProcessing)}/month`,
    "Estimated monthly saving": `${gbp(results.processingSaving)} (${results.savingPercent}%)`,
    "Estimated annual saving": gbp(results.annualSaving),
    "Estimated monthly transactions": Math.round(results.txnCount),
    "Other monthly costs (not in comparison)": results.hasOtherCosts ? `${gbp(results.otherCosts)}/month` : null,
  };
}

/** "Debit 0.8% · Credit 1.6% · Auth £0.05/txn · Terminal £20.00/mo" from what was entered. */
function currentRates(ctx) {
  const debit = num(ctx[K.debit]);
  const credit = num(ctx[K.credit]);
  const auth = num(ctx[K.auth]);
  const terminal = num(ctx[K.terminal]);
  const pci = num(ctx[K.pci]);
  const other = num(ctx[K.other]);
  return [
    debit && `Debit ${debit}%`,
    credit && `Credit ${credit}%`,
    auth && `Auth ${money(auth)}/txn`,
    terminal && `Terminal ${money(terminal)}/mo`,
    pci && `PCI ${money(pci)}/mo`,
    other && `Other fees ${money(other)}/mo`,
  ].filter(Boolean).join(" · ");
}

const toolSavings = {
  id: "tool_savings",
  label: "Savings Calculator",
  crm: { layout: "Standard", leadSource: "Website Calculator - Savings" },
  fields: [
    [fullName, company],
    [email, { ...phone, help: "We'll only call to discuss your results — never spam." }],
    currentProvider,
  ],
  submitLabel: "Send my review",
  consent: { variant: "standard" },
  success: { title: "Thank you", body: "We'll be in touch with your personalised review shortly." },
  contextToCrm: (ctx) => ({
    Monthly_Card_Turnovers: num(ctx[K.turnover]),
    ATV: num(ctx[K.atv]),
    Current_rates: currentRates(ctx),
  }),
  summary: (values, ctx) =>
    ctx[K.current] && ctx[K.proposed]
      ? `Savings Calculator: currently ${ctx[K.current]} vs CanDo ${ctx[K.proposed]}`
      : "Savings Calculator",
};

export default toolSavings;
